/**
 * #856: the unsent Main Command state an update reload carries across, in
 * this tab only. Written just before the reload, read and removed once on the
 * next load, and applied only to the same organization it came from.
 */
import { SELECTABLE_REPLY_TARGETS, type MainCommandReplyTarget } from './main-command-recipients';
import { parseReplyRef, type MainCommandReplyDraft } from './main-command-reply';
import type { NoteHandoffStage } from './note-handoff';
import type { CommandDiscussionMode } from './shared-command-timeline';

const KEY = 'brunel.update.mainCommand';
const CONTENT_LIMIT = 16_000;

export interface MainCommandReloadState {
  readonly orgId: string;
  readonly content: string;
  readonly replyTargets: readonly MainCommandReplyTarget[];
  readonly workerReplyTargets: readonly string[];
  readonly replyingTo: MainCommandReplyDraft | null;
  readonly noteHandoff: NoteHandoffStage | null;
  readonly chatMode: CommandDiscussionMode;
}

/** True when there is anything here a reload would lose. */
export function mainCommandHasUnsent(state: MainCommandReloadState): boolean {
  return state.content !== ''
    || state.replyingTo !== null
    || state.noteHandoff !== null
    || state.chatMode.kind !== 'command'
    || state.workerReplyTargets.length > 0
    || state.replyTargets.length !== 1 || state.replyTargets[0] !== 'overseer';
}

export function saveMainCommandForReload(storage: Storage | null, state: MainCommandReloadState): boolean {
  try {
    if (mainCommandHasUnsent(state)) {
      if (storage === null) return false;
      storage.setItem(KEY, JSON.stringify({ v: 1, ...state }));
    } else storage?.removeItem(KEY);
    return true;
  } catch { return false; }
}

const text = (value: unknown): value is string => typeof value === 'string';

function replyDraft(value: unknown): MainCommandReplyDraft | null {
  if (value === null || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const ref = parseReplyRef(row['ref']);
  return ref !== null && text(row['author']) && text(row['text'])
    ? { ref, author: row['author'], text: row['text'] }
    : null;
}

function noteStage(value: unknown): NoteHandoffStage | null {
  if (value === null || typeof value !== 'object') return null;
  const row = value as Record<string, unknown>;
  const chip = row['chip'] as Record<string, unknown> | null | undefined;
  if (!text(row['text']) || chip === null || typeof chip !== 'object') return null;
  if (!text(chip['noteId']) || !text(chip['title']) || !text(chip['preview']) || typeof chip['truncated'] !== 'boolean') return null;
  return {
    text: row['text'].slice(0, CONTENT_LIMIT),
    chip: { noteId: chip['noteId'], title: chip['title'], preview: chip['preview'], truncated: chip['truncated'] },
    section: 'control',
    autoSend: false,
    recipientOverride: null,
  };
}

function discussion(value: unknown): CommandDiscussionMode {
  if (value === null || typeof value !== 'object') return { kind: 'command' };
  const row = value as Record<string, unknown>;
  const threadId = row['threadId'];
  return row['kind'] === 'discussion' && text(row['title']) && (threadId === null || text(threadId))
    ? { kind: 'discussion', threadId, title: row['title'], source: 'observer' }
    : { kind: 'command' };
}

/** Reads and removes the carried state; null unless it is well formed and for this organization. */
export function takeMainCommandAfterReload(storage: Storage | null, orgId: string): MainCommandReloadState | null {
  let raw: string | null = null;
  try {
    raw = storage?.getItem(KEY) ?? null;
    storage?.removeItem(KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  let row: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (parsed === null || typeof parsed !== 'object') return null;
    row = parsed as Record<string, unknown>;
  } catch {
    return null;
  }
  if (row['v'] !== 1 || row['orgId'] !== orgId || !text(row['content'])) return null;
  // A selection saved by an earlier build may name an Observer; only a recipient still offered is restored.
  const targets = Array.isArray(row['replyTargets'])
    ? SELECTABLE_REPLY_TARGETS.filter((target) => (row['replyTargets'] as unknown[]).includes(target))
    : [];
  const workers = Array.isArray(row['workerReplyTargets'])
    ? (row['workerReplyTargets'] as unknown[]).filter(text)
    : [];
  return {
    orgId,
    content: row['content'].slice(0, CONTENT_LIMIT),
    replyTargets: targets,
    workerReplyTargets: workers,
    replyingTo: replyDraft(row['replyingTo']),
    noteHandoff: noteStage(row['noteHandoff']),
    chatMode: discussion(row['chatMode']),
  };
}
